import { omit } from "lodash";
import {
  DiscoveryData,
  DiscoveryDetails,
  DiscoveryRow,
  DiscoveryStatus,
  FeedbackReason,
} from "@radarly/models/discovery";
import {
  InsightPage,
  InsightPageRecurringSnapshot,
  InsightPageSnapshot,
  InsightPageSnapshotFactory,
  LeanInsightPage,
  RecurringSnapshotPayload,
} from "@radarly/models/insight-pages/model";
import { PublicPage } from "@radarly/models/public-pages";
import { Pagination } from "@radarly/models/search";
import { SharedPage } from "@radarly/models/shared-pages";
import { RadarlyAPI } from "./base";

export class InsightPagesAPI extends RadarlyAPI {
  public getInsightPages(projectId: number, workspaceId: number) {
    return this.get<InsightPage[]>(
      `/projects/${projectId}/dashboards/${workspaceId}/custom-pages.json`
    );
  }

  public getLeanInsightPages(
    projectId: number,
    workspaceId: number,
    pagination: Pagination
  ) {
    return this.post<LeanInsightPage[]>(
      `/projects/${projectId}/custom-pages/list-basic.json`,
      {
        filter: {
          dashboardIds: workspaceId ? [workspaceId] : null,
          titleLike: "",
          includeProjectLevel: !workspaceId,
        },
        sort: [
          {
            // global first
            by: "projectLevel",
            order: "desc",
          }, // sort by workspace label
          {
            by: "dashboardLabel",
            order: "asc",
          },
          {
            // when same label, sort by id
            by: "dashboardId",
            order: "asc",
          },
          {
            // sort by IP title when same workspace
            by: "title",
            order: "asc",
          },
        ],
        pagination,
      }
    );
  }

  public batchUpdateRecurringSnapshots(
    projectId: number,
    page: InsightPage["id"],
    snapshots: Array<{
      id: InsightPageRecurringSnapshot["id"];
      page: InsightPage;
    }>
  ) {
    return this.put<void>(
      `/projects/${projectId}/custom-pages/${page}/batch/planned.json`,
      snapshots
    );
  }
  public getInsightPage(projectId: number, page: InsightPage["id"]) {
    return this.get<InsightPage>(
      `/projects/${projectId}/custom-pages/${page}.json`
    );
  }

  public setInsightPage(
    projectId: number,
    page: InsightPage,
    template?: string
  ) {
    if (page.id) {
      return this.put<InsightPage>(
        `/projects/${projectId}/custom-pages/${page.id}.json`,
        page
      );
    } else {
      return this.post<InsightPage>(
        `/projects/${projectId}/custom-pages.json` +
          (template ? "?template=" + template : ""),
        omit(page, "id")
      );
    }
  }
  public deleteInsightPage(projectId: number, page: InsightPage["id"]) {
    return this.delete<{ deleted: boolean }>(
      `/projects/${projectId}/custom-pages/${page}.json`
    );
  }
  public sendSnapshot(
    projectId: number,
    page: InsightPage["id"],
    snapshot: InsightPageSnapshotFactory
  ) {
    return this.post<InsightPageSnapshot>(
      `/projects/${projectId}/snapshot-custom-pages/${page}.json`,
      snapshot
    );
  }

  public sendRecurringSnapshot(
    projectId: number,
    pageId: InsightPage["id"],
    snapshot: RecurringSnapshotPayload
  ) {
    if (snapshot.id) {
      return this.put<InsightPageRecurringSnapshot>(
        `/projects/${projectId}/custom-pages/${pageId}/planned/${snapshot.id}.json`,
        snapshot
      );
    } else {
      return this.post<InsightPageRecurringSnapshot>(
        `/projects/${projectId}/custom-pages/${pageId}/planned.json`,
        omit(snapshot, "id")
      );
    }
  }

  public getShareInsightPage(s3id: string) {
    return this.get<PublicPage>(`/snapshot-custom-pages/${s3id}.json`, {
      withAuthorization: false,
    });
  }
  public shareInsightPageAccess(
    projectId: number,
    page: InsightPage["id"],
    access: InsightPage["access"],
    sharedWith: InsightPage["sharedWith"]
  ) {
    return this.post<InsightPage>(
      `/projects/${projectId}/custom-pages/${page}/share-with.json`,
      { access, sharedWith }
    );
  }

  public getSharedPage(id: InsightPage["id"]) {
    return this.get<SharedPage>(`/shared-custom-page/${id}.json`);
  }

  public getRecurringSnapshots(
    projectId: number,
    page: InsightPage["id"],
    all?: boolean
  ) {
    return this.get<InsightPageRecurringSnapshot[]>(
      `/projects/${projectId}/custom-pages/${page}/planned.json` +
        (all ? "?all=true" : "")
    );
  }

  public deleteRecurringSnapshot(
    projectId: number,
    page: InsightPage["id"],
    recurringSnapshot: InsightPageRecurringSnapshot["id"]
  ) {
    return this.delete<boolean>(
      `/projects/${projectId}/custom-pages/${page}/planned/${recurringSnapshot}.json`
    );
  }

  public getInvalidRecurringSnapshots(
    projectId: number,
    page: InsightPage["id"],
    timezone: InsightPage["timezone"]
  ) {
    return this.get<InsightPageRecurringSnapshot[]>(
      `/projects/${projectId}/custom-pages/${page}/invalid-planned.json?tz=${timezone}`
    );
  }

  public getDiscovery(
    projectId: number,
    workspaceId: number,
    params?: Record<string, unknown>
  ) {
    return this.get<DiscoveryData>(
      `/projects/${projectId}/dashboards/${workspaceId}/discovery-page.json` +
        (params
          ? `?` +
            (params.period ? `period=${String(params.period)}&` : "") +
            (params.id ? `id=${String(params.id)}` : "")
          : "")
    );
  }

  public getDiscoveryStatus(
    projectId: number,
    workspaceId: number,
    params?: Record<string, unknown>
  ) {
    return this.get<DiscoveryStatus>(
      `/projects/${projectId}/dashboards/${workspaceId}/discovery-page/is-fresh.json` +
        (params
          ? `?` +
            (params.period ? `period=${String(params.period)}&` : "") +
            (params.id ? `id=${String(params.id)}` : "")
          : "")
    );
  }

  public getDiscoveryDetails(
    projectId: number,
    workspaceId: number,
    meta: DiscoveryRow["meta"],
    template: string = "default"
  ) {
    return this.post<DiscoveryDetails>(
      `/projects/${projectId}/dashboards/${workspaceId}/discovery-details.json?template=${template}`,
      { ...meta }
    );
  }

  public sendDiscoveryFeedback(
    projectId: number,
    workspaceId: number,
    page: InsightPage,
    feedback: boolean,
    reason: FeedbackReason,
    message?: string
  ) {
    return this.post<void>(
      `/projects/${projectId}/dashboards/${workspaceId}/discovery-feedback.json`,
      { feedback, message, reason, page }
    );
  }

  public searchPages(
    projectId: number,
    search: string,
    workspaceId: number,
    pagination: Pagination,
    sort: string
  ) {
    return this.post<InsightPage[]>(
      `/projects/${projectId}/custom-pages/list.json`,
      {
        filter: {
          dashboardIds: workspaceId ? [workspaceId] : null,
          titleLike: search,
          includeProjectLevel: !workspaceId,
        },
        sort: [
          {
            // global first
            by: "projectLevel",
            order: "desc",
          }, // sort by workspace label
          {
            by: "dashboardLabel",
            order: "asc",
          },
          {
            // when same label, sort by id
            by: "dashboardId",
            order: "asc",
          },
          {
            // sort by IP title when same workspace
            by: "title",
            order: "asc",
          },
        ],
        pagination,
      }
    );
  }

  public saveDiscoverySnapshot(projectId: number, snapshot: InsightPage) {
    return this.post<{ s3Id: string }>(
      `/projects/${projectId}/snapshot-custom-page.json`,
      snapshot
    );
  }
}
